import React, { useContext, useCallback, useState, useEffect  } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAPI } from "../context/ApiContext";
import { SnackbarContext } from "../context/SnackbarContext";
import Createpurepost from "../components/CreatePurepost";
import PostFabIcon from "../assets/post_fab_icon.png";
import NotificationModal from "../components/NotificationModal";

const DEFAULT_AVATAR = "/assets/images/default_profile.png";

const Header = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { newSnack } = useContext(SnackbarContext);

  const { isAuthenticated, profile, me, myUserId, getNotifications, getUnreadNotificationsCount } = useAPI();

  // 自分のプロフィールリンク
  const myUid = me?.userId || myUserId || profile?.userId || "";
  const myProfileHref = isAuthenticated
    ? (myUid ? `/user/${encodeURIComponent(myUid)}` : "/home")
    : "/login";

  /* ------ Post モーダル ------ */
  const [openCreate, setOpenCreate] = useState(false);
  const openPostModal = () => {
    if (!isAuthenticated) {
      history.push("/login");
      return;
    }
    setOpenCreate(true);
  };
  const closePostModal = () => setOpenCreate(false);

  const onPosted = useCallback(
    (created) => {
      closePostModal();
      try {
        window.dispatchEvent(new CustomEvent("nl:new-post", { detail: created }));
      } catch {}
      const pid = created?.postId || created?.id;
      if (pid) history.push(`/post/${pid}`);
      else newSnack("success", t("Posted") || "投稿しました");
    },
    [history, newSnack, t]
  );

  /* ------ 通知（モーダル） ------ */
  const [notiModalOpen, setNotiModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const openNoti = () => {
    if (!isAuthenticated) {
      newSnack("error", t("Header14") || "ログインが必要です");
      return;
    }
    setNotiModalOpen(true);
  };

  const handlePrimaryAction = () => {
    if (isAuthenticated) openPostModal();
    else history.push("/login");
  };

  useEffect(() => {
    let mounted = true;
    const boot = async () => {
      if (!isAuthenticated || !me?.userId) { if (mounted) setUnreadCount(0); return; }
      try {
   if (typeof getUnreadNotificationsCount === "function") {
     const r = await getUnreadNotificationsCount(me.userId);
     if (mounted) setUnreadCount(Number(r?.unreadCount) || 0);
   } else {
          const res = await getNotifications(me.userId, { count: 0 });
          const list = Array.isArray(res?.res) ? res.res : [];
          const unread = list.reduce((acc, n) => acc + (n.read ? 0 : 1), 0);
          if (mounted) setUnreadCount(unread);
   }
      } catch {}
    };
    boot();
    return () => { mounted = false; };
  }, [isAuthenticated, me?.userId, getNotifications, getUnreadNotificationsCount]);


  // すでにある openPostModal はそのまま利用
useEffect(() => {
  const onIntent = () => openPostModal();
  window.addEventListener("nl:open-post-intent", onIntent);

  // URLクエリでも起動（/home?openPost=1）
  if (new URLSearchParams(window.location.search).get("openPost") === "1") {
    onIntent();
  }
  return () => window.removeEventListener("nl:open-post-intent", onIntent);
}, [isAuthenticated]); 


  return (
    <div className="nav-header bg-white shadow-xs border-0">
      {/* 上部バー */}
      <div className="nav-top d-flex align-items-center justify-content-between w-100">
        {/* 左：ロゴ */}
        <Link to="/home" className="d-flex align-items-center">
          <img
            src="/assets/images/nicelinks_icon.png"
            alt="logo"
            className="card p-0 h30 bg-transparent me-2 ms-2 border-0"
          />
          <span className="fredoka-font ls-3 fw-600 text-current font-xxl logo-text mb-0">
            {t("Header4")}
          </span>
          <span className="ms-3 mt-0 fw-600 text-current menu-icon font-xsssss mb-0 d-none d-md-inline">
            {t("Header23")}
          </span>
        </Link>

        {/* 右：Post / 通知 / プロフィール */}
        <div className="d-flex align-items-center ms-auto" style={{ gap: 10 }}>
          {/* Post / Login */}
          <button
            type="button"
            onClick={handlePrimaryAction}
            className="btn btn-sm bg-current text-white rounded-xxl d-flex align-items-center"
            aria-label={isAuthenticated ? "Create post" : "Login"}
            title={isAuthenticated ? (t("Post") || "Post") : (t("Login") || "ログイン")}
          >
            <span
              style={{
                width: 20,
                height: 20,
                display: "inline-block",
                backgroundImage: `url(${PostFabIcon})`,
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
            <span className="ms-1 d-none d-md-inline">
              {isAuthenticated ? (t("Post") || "Post") : (t("Login") || "ログイン")}
            </span>
          </button>

          {/* 通知ベル（バッジは unreadCount） */}
          <span className="pointer position-relative" onClick={openNoti} aria-label="Notifications">
            <i className="feather-bell font-xl text-current"></i>
            {unreadCount > 0 && (
              <span
                className="position-absolute bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center"
                style={{ top: -6, right: -6, width: 18, height: 18, fontSize: 10, fontWeight: 700 }}
              >
                {unreadCount}
              </span>
            )}
          </span>

          {/* プロフィール */}
          <Link
            to={myProfileHref}
            className="pointer d-flex align-items-center"
            onClick={(e) => {
              if (isAuthenticated && !myUid) {
                e.preventDefault();
                newSnack("error", t("LoadingProfile") || "プロフィールを読み込み中です");
              }
            }}
            aria-label="My profile"
          >
            <img
              src={profile?.image || DEFAULT_AVATAR}
              alt="user"
              className="w40 shadow-xss rounded-circle"
              onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
            />
          </Link>
        </div>
      </div>

      {/* 左サイドナビ（既存そのまま） */}
      <nav className="navigation scroll-bar">
        <div className="container ps-0 pe-0">
          <div className="nav-content">
            <div className="nav-wrap bg-white bg-transparent-card rounded-xxl shadow-xss pt-3 pb-1 mb-2 mt-2">
              <div className="nav-caption fw-600 font-xssss text-grey-500">
                <span></span>{t("Header7")}
              </div>
              <ul className="mb-1 top-content">
                <li className="logo d-none d-xl-block d-lg-block"></li>
                <li>
                  <Link to="/home" className="nav-content-bttn open-font">
                    <img
                      src="/assets/images/nicelinks_icon.png"
                      alt="logo"
                      className="btn-round-md p-1 bg-white me-3 shadow-sm border-light"
                    />
                    <span>{t("Header8")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/postsearch" className="nav-content-bttn open-font">
                    <i className="feather-search btn-round-md bg-orange me-3"></i>
                    <span>{t("Header10")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/usersearch" className="nav-content-bttn open-font">
                    <i className="feather-users btn-round-md bg-current me-3"></i>
                    <span>{t("Header9")}</span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className="nav-wrap bg-white bg-transparent-card rounded-xxl shadow-xss pt-3 pb-1">
              <div className="nav-caption fw-600 font-xssss text-grey-500">
                <span></span> {t("Header11")}
              </div>
              <ul className="mb-1">
                <li className="logo d-none d-xl-block d-lg-block"></li>
                <li>
                  <Link to="/howto" className="nav-content-bttn open-font h-auto pt-2 pb-2">
                    <i className="font-sm feather-info me-3 text-grey-500"></i>
                    <span>{t("Header19")}</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={isAuthenticated && myUid ? `/user/${encodeURIComponent(myUid)}?tab=bookmarks` : "/login"}
                    className="nav-content-bttn open-font h-auto pt-2 pb-2"
                    onClick={(e) => {
                      if (isAuthenticated && !myUid) {
                        e.preventDefault();
                        newSnack("error", t("LoadingProfile") || "プロフィールを読み込み中です");
                      }
                    }}
                    aria-label="My bookmarks"
                  >
                    <i className="font-sm feather-bookmark me-3 text-grey-500"></i>
                    <span>{t("Userpage3") || "ブックマーク"}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/adapplication" className="nav-content-bttn open-font h-auto pt-2 pb-2">
                    <i className="font-sm feather-radio me-3 text-grey-500"></i>
                    <span>{t("Header15")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/settings" className="nav-content-bttn open-font h-auto pt-2 pb-2">
                    <i className="font-sm feather-settings me-3 text-grey-500"></i>
                    <span>{t("Header13")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/Notations-SCTA" className="nav-content-bttn open-font h-auto pt-2 pb-2">
                    <h2 className="fw-500 font-xssss me-3 text-grey-500 text-decoration-underline pointer">
                      {t("Header18")}
                    </h2>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      {/* ヘッダー内検索（既存） */}
      <div className="app-header-search">
        <form className="search-form">
          <div className="form-group searchbox mb-0 border-0 p-1">
            <input type="text" className="form-control border-0" placeholder="Search..." />
            <i className="input-icon">
              <ion-icon name="search-outline" role="img" className="md hydrated" aria-label="search outline"></ion-icon>
            </i>
            <span className="ms-1 mt-1 d-inline-block close searchbox-close">
              <i className="ti-close font-xs"></i>
            </span>
          </div>
        </form>
      </div>

      {/* Post 作成モーダル */}
      <Createpurepost
  isVisible={openCreate}
  handleClose={closePostModal}
  onPosted={onPosted}
  initialUrl={window.__nl_intent_url || ""}
    />

      {/* 右下の投稿FAB（ログイン時のみ） */}
      {isAuthenticated && (
        <>
          <style>{`
            .nl-post-fab {
              position: fixed;
              right: 16px;
              bottom: 24px;
              width: 56px;
              height: 56px;
              border: 0;
              border-radius: 50%;
              background: #2a6cf5;
              box-shadow: 0 8px 24px rgba(0,0,0,.2);
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              z-index: 1100;
            }
            .nl-post-fab__icon {
              width: 28px;
              height: 28px;
              background-repeat: no-repeat;
              background-position: center;
              background-size: contain;
              display: inline-block;
            }
            @media (max-width: 768px) { .nl-post-fab { bottom: 88px; right: 16px; } }
          `}</style>

          <button
            type="button"
            className="nl-post-fab"
            onClick={openPostModal}
            aria-label="Create post"
            title={t("Post") || "Post"}
          >
            <span className="nl-post-fab__icon" style={{ backgroundImage: `url(${PostFabIcon})` }} />
          </button>
        </>
      )}

      {/* 通知モーダル（ここで未読数を受け取ってバッジ更新） */}
      <NotificationModal
        isVisible={notiModalOpen}
        handleClose={() => setNotiModalOpen(false)}
        onUnreadChange={setUnreadCount}
      />
    </div>
  );
};

export default Header;
